import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";

const UserAuthContext = createContext(null);

const STORAGE_KEY = "cmz_user_auth"; // stores user only; token now lives in an HttpOnly cookie

export function UserAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // --- helpers --------------------------------------------------------------

  const clearStoredAuth = useCallback(() => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.error("Failed to clear auth state from storage:", err);
    }
  }, []);

  const persistAuthState = useCallback((nextUser) => {
    setToken(null);
    setUser(nextUser || null);

    try {
      if (nextUser) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ user: nextUser }));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch (err) {
      console.error("Failed to write auth state to storage:", err);
    }
  }, []);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.user) {
          setToken(null);
          setUser(parsed.user);
        }
      }
    } catch (err) {
      console.error("Failed to read auth state from storage:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(
    async (email, password) => {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        const message = data && data.error ? data.error : "Login failed";
        throw new Error(message);
      }

      if (!data.user) {
        throw new Error("Invalid login response from server");
      }

      persistAuthState(data.user);
      return data;
    },
    [persistAuthState]
  );

  const logout = useCallback(async () => {
    // Best-effort server logout: auth cookie is sent automatically on same-origin requests
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
    } catch (err) {
      console.error("Logout request failed (non-fatal):", err);
    }

    // Always clear local auth state
    persistAuthState(null);
  }, [persistAuthState]);

  const sendLogoutBeacon = useCallback(() => {
    try {
      if (!navigator.sendBeacon) {
        return;
      }

      const blob = new Blob([JSON.stringify({})], { type: "application/json" });
      navigator.sendBeacon("/api/auth/logout-beacon", blob);
    } catch (err) {
      console.error("Failed to send logout beacon:", err);
    }
  }, []);

  // Best-effort: when tab/app is closed, send logout beacon so backend
  // can mark logout_at, and clear stored auth so next open is logged out.
  useEffect(() => {
    const handleUnload = () => {
      try {
        sendLogoutBeacon();
      } catch (e) {
        // ignore
      }
      // Clear browser-side auth so reopening the app doesn't auto-log in
      clearStoredAuth();
    };

    window.addEventListener("unload", handleUnload);
    return () => {
      window.removeEventListener("unload", handleUnload);
    };
  }, [sendLogoutBeacon, clearStoredAuth]);

  const value = {
    user,
    token,
    isAuthenticated: !!user,
    loading,
    login,
    logout,
  };

  return (
    <UserAuthContext.Provider value={value}>
      {children}
    </UserAuthContext.Provider>
  );
}

export function useUserAuth() {
  const ctx = useContext(UserAuthContext);
  if (!ctx) {
    throw new Error("useUserAuth must be used within a UserAuthProvider");
  }
  return ctx;
}
